// src/pages/Portfolio.jsx
import React, { useState, useEffect } from 'react';
import { ExternalLink, Filter, Search, X, Zap, ArrowRight } from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';

const Portfolio = () => {
    const [portfolioData, setPortfolioData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProjects, setFilteredProjects] = useState([]);

    useEffect(() => {
        const fetchPortfolioData = async () => {
            try {
                const response = await fetch('/api/content/portfolio');
                const data = await response.json();
                setPortfolioData(data);
                setFilteredProjects(data.projects || []);
            } catch (error) {
                console.error('Error loading portfolio data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPortfolioData();
    }, []);

    useEffect(() => {
        if (!portfolioData?.projects) return;

        let filtered = portfolioData.projects;

        // Apply category filter
        if (filter !== 'all') {
            filtered = filtered.filter(project => project.category === filter);
        }

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(project =>
                project.title.toLowerCase().includes(term) ||
                project.description.toLowerCase().includes(term) ||
                project.client.toLowerCase().includes(term) ||
                project.tags.some(tag => tag.toLowerCase().includes(term))
            );
        }

        setFilteredProjects(filtered);
    }, [filter, searchTerm, portfolioData]);

    const clearFilters = () => {
        setFilter('all');
        setSearchTerm('');
    };

    if (loading) {
        return (
            <SEOLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-900">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
                </div>
            </SEOLayout>
        );
    }

    const categories = portfolioData?.categories || [];
    const featuredProjects = portfolioData?.projects?.filter(p => p.featured) || [];

    return (
        <SEOLayout
            title="Solutions"
            description={portfolioData?.hero?.description}
            keywords="Baraqu LLC solutions, technology portfolio, VR development, specialized hardware, systematic innovation"
        >
            {/* Hero Section */}
            <section className="relative bg-gray-900 overflow-hidden" aria-labelledby="portfolio-heading">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <Zap className="h-12 w-12 text-blue-400" />
                        </div>
                        <h1 id="portfolio-heading" className="text-5xl md:text-6xl font-light text-white mb-6">
                            {portfolioData?.hero?.title}
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            {portfolioData?.hero?.description}
                        </p>
                    </div>
                </div>

                {/* Floating geometric elements */}
                <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-blue-400/20 rotate-45"></div>
                <div className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-blue-400/10 rotate-12"></div>
            </section>

            {/* Stats Section */}
            {portfolioData?.stats && (
                <section className="py-16 bg-white" aria-labelledby="stats-heading">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 id="stats-heading" className="sr-only">Portfolio Statistics</h2>
                        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
                            {Object.entries(portfolioData.stats).map(([key, value]) => (
                                <div key={key} className="text-center">
                                    <div className="text-4xl font-light text-blue-600 mb-2">{value}</div>
                                    <div className="text-sm text-gray-600 capitalize font-medium">
                                        {key.replace(/_/g, ' ')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Featured Projects Section */}
            {featuredProjects.length > 0 && (
                <section className="py-20 bg-gray-50" aria-labelledby="featured-heading">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 id="featured-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                                Featured Solutions
                            </h2>
                            <p className="text-xl text-gray-600">
                                Breakthrough innovations that demonstrate systematic thinking
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                            {featuredProjects.slice(0, 6).map((project) => (
                                <article key={project.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 border border-gray-200">
                                    <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                                        <img
                                            src={project.image}
                                            alt={`${project.title} solution`}
                                            className="w-full h-48 object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center space-x-2 mb-3">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                {categories.find(c => c.id === project.category)?.name || project.category}
                                            </span>
                                            <span className="text-sm text-gray-500">{project.year}</span>
                                            {project.featured && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    Featured
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-xl font-medium text-gray-900 mb-3">
                                            {project.title}
                                        </h3>
                                        <p className="text-gray-600 mb-4 line-clamp-3">
                                            {project.description}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {project.tags.slice(0, 3).map((tag, index) => (
                                                <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    {tag}
                                                </span>
                                            ))}
                                            {project.tags.length > 3 && (
                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    +{project.tags.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm text-gray-500">
                                                <span className="font-medium">Client:</span> {project.client}
                                            </p>
                                            {project.url && (
                                                <a
                                                    href={project.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors duration-200"
                                                    aria-label={`View ${project.title} project details`}
                                                >
                                                    Learn More
                                                    <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Filter and Search Section */}
            <section className="bg-white py-12" aria-labelledby="filter-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 id="filter-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-8 text-center">
                        All Solutions ({filteredProjects.length})
                    </h2>

                    <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between mb-12">
                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setFilter('all')}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    filter === 'all'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                                }`}
                                aria-pressed={filter === 'all'}
                            >
                                All Solutions
                            </button>
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setFilter(category.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                        filter === category.id
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                                    }`}
                                    aria-pressed={filter === category.id}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>

                        {/* Search and Clear */}
                        <div className="flex gap-3 w-full lg:w-auto">
                            <div className="relative flex-1 lg:min-w-0 lg:w-80">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search solutions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                    aria-label="Search solutions"
                                />
                            </div>

                            {/* Clear Filters */}
                            {(filter !== 'all' || searchTerm) && (
                                <button
                                    onClick={clearFilters}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                                >
                                    <X className="h-4 w-4 mr-1" aria-hidden="true" />
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Projects Grid */}
            <section className="py-16 bg-gray-50" aria-labelledby="all-projects-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 id="all-projects-heading" className="sr-only">All Solutions</h2>

                    {filteredProjects.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="text-gray-500 text-xl mb-4">
                                No solutions found matching your criteria.
                            </div>
                            <button
                                onClick={clearFilters}
                                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                            >
                                View All Solutions
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                            {filteredProjects.map((project) => (
                                <article key={project.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 border border-gray-200">
                                    <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                                        <img
                                            src={project.image}
                                            alt={`${project.title} solution`}
                                            className="w-full h-48 object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center space-x-2 mb-3">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                {categories.find(c => c.id === project.category)?.name || project.category}
                                            </span>
                                            <span className="text-sm text-gray-500">{project.year}</span>
                                            {project.featured && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    Featured
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-xl font-medium text-gray-900 mb-3">
                                            {project.title}
                                        </h3>
                                        <p className="text-gray-600 mb-4">
                                            {project.description}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {project.tags.map((tag, index) => (
                                                <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm text-gray-500">
                                                <span className="font-medium">Client:</span> {project.client}
                                            </p>
                                            {project.url && (
                                                <a
                                                    href={project.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors duration-200"
                                                    aria-label={`View ${project.title} solution details`}
                                                >
                                                    Learn More
                                                    <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Methodology Section */}
            <section className="py-20 bg-gray-900" aria-labelledby="methodology-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="methodology-heading" className="text-3xl md:text-4xl font-light text-white mb-4">
                            {portfolioData?.methodology?.title}
                        </h2>
                        <p className="text-xl text-gray-300">
                            {portfolioData?.methodology?.subtitle}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {portfolioData?.methodology?.principles?.map((principle, index) => (
                            <div key={index} className="text-center">
                                <div className="bg-gray-800 p-6 rounded-lg mb-4">
                                    <h3 className="text-lg font-medium text-white mb-3">
                                        {principle.name}
                                    </h3>
                                    <p className="text-gray-300 text-sm leading-relaxed">
                                        {principle.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-white" aria-labelledby="portfolio-cta-heading">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center mb-6">
                        <Zap className="h-12 w-12 text-blue-600" />
                    </div>
                    <h2 id="portfolio-cta-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                        Ready to Create Your Solution?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        From concept to completion, we provide systematic innovation that transforms challenges into lasting solutions.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="/contact"
                            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                        >
                            Start a Conversation
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </a>
                        <a
                            href="/about"
                            className="inline-flex items-center px-8 py-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors duration-200"
                        >
                            Learn Our Approach
                        </a>
                    </div>
                </div>
            </section>
        </SEOLayout>
    );
};

export default Portfolio;